import React from 'react';
import {
  createStyles,
  makeStyles,
  Typography,
  Box,
  Stepper,
  StepIconProps,
  Theme,
  Step,
  StepLabel,
} from '@material-ui/core';
import { useTranslation } from '../../../../../hooks/useTranslation';
import colors from '../../../../../themes/main-colors';
import clsx from 'clsx';
import { withStyles } from '@material-ui/styles';
import StepConnector from '@material-ui/core/StepConnector';
import { Maybe } from '../../../../../__generated__/pge-types';

const RESTORATION_STATUS = {
  crewDispatched: ['RCVD', 'ASN', 'ENR', 'SNT'],
  crewOnsite: ['ONS'],
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    stepper: {
      background: 'none',
      padding: 0,
      marginTop: '15px',
      marginBottom: '15px',
    },
    stepsName: {
      fontSize: '0.75rem',
    },
    stepIconRoot: {
      backgroundColor: '#fff',
      border: `2px solid ${colors.sparkBlue}`,
      borderRadius: '50%',
      height: '10px',
      width: '10px',
    },
    active: {
      backgroundColor: colors.sparkBlue,
    },
    activeDashboardAlt: {
      backgroundColor: colors.orange,
      borderColor: colors.orange,
    },
    completed: {
      backgroundColor: colors.darkGray3,
      borderColor: colors.darkGray3,
    },
    activeStep: {
      color: colors.sparkBlue,
      fontWeight: 'bold',
    },
    activeStepDashboardAlt: {
      fontWeight: 'bold',
    },
    completedStep: {
      color: colors.darkGray3,
    },
    title: {
      fontWeight: 'bold',
    },
  }),
);

const StatusConnector = withStyles({
  root: {
    padding: 0,
    marginLeft: '3px',
    minHeight: '20px',
  },
  active: {
    '& $line': {
      borderColor: colors.darkGray3,
    },
  },
  completed: {
    '& $line': {
      borderColor: colors.darkGray3,
    },
  },
  line: {
    borderLeftStyle: 'dashed',
    borderWidth: '3px',
    borderColor: colors.sparkBlue,
  },
})(StepConnector);

const DetailStepIcon = (
  props: StepIconProps & {
    isDashboard?: boolean;
  },
) => {
  const classes = useStyles();
  const { active, completed, isDashboard } = props;
  return (
    <div
      className={clsx(classes.stepIconRoot, {
        [isDashboard ? classes.activeDashboardAlt : classes.active]: active,
        [classes.completed]: completed,
      })}
    ></div>
  );
};

export enum ACTIVE_RESTORATION_STEP {
  POWER_OUT = 0,
  CREW_DISPATCHED = 1,
  CAUSE_DETERMINED = 2,
  CREW_ONSITE = 3,
  POWER_RESTORED = 4,
}

const pickActiveStep = ({
  isCrewOnsite,
  crewDispatchedStatus,
  cause,
  eventStatus,
}: Props) => {
  //use eventStatus only if it is available. Few API's didn't have it yet(getOutageLookup, getOutageDetailsByZipCode,. etc )
  if (
    typeof eventStatus !== 'undefined' &&
    RESTORATION_STATUS.crewOnsite.includes(String(eventStatus))
  ) {
    return ACTIVE_RESTORATION_STEP.CREW_ONSITE;
  }

  if (typeof eventStatus === 'undefined' && isCrewOnsite) {
    return ACTIVE_RESTORATION_STEP.CREW_ONSITE;
  }

  if (
    cause &&
    cause.toLowerCase() !== 'under investigation' &&
    cause.toLowerCase() !== 'investigating' &&
    cause.toLowerCase().indexOf('investigating') < 0
  ) {
    return ACTIVE_RESTORATION_STEP.CAUSE_DETERMINED;
  }

  if (
    typeof eventStatus !== 'undefined' &&
    RESTORATION_STATUS.crewDispatched.includes(String(eventStatus))
  ) {
    return ACTIVE_RESTORATION_STEP.CREW_DISPATCHED;
  }

  if (
    typeof eventStatus === 'undefined' &&
    crewDispatchedStatus &&
    crewDispatchedStatus !== 'Pending Assignment'
  ) {
    return ACTIVE_RESTORATION_STEP.CREW_DISPATCHED;
  }

  return ACTIVE_RESTORATION_STEP.POWER_OUT;
};
interface Props {
  crewDispatchedStatus?: Maybe<string>;
  isCrewOnsite?: Maybe<boolean>;
  cause?: Maybe<string>;
  eventStatus?: Maybe<string>;
  isDashboard?: boolean;
  setActiveStep?: React.Dispatch<React.SetStateAction<ACTIVE_RESTORATION_STEP>>;
}

export const RestorationStatus = (props: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const steps = [
    t('POWER_OUT'),
    t('CREW_DISPATCHED'),
    t('CAUSE_DETERMINED'),
    t('CREW_ONSITE'),
    t('POWER_RESTORED'),
  ];
  const activeStep = pickActiveStep(props);
  props?.setActiveStep?.(activeStep);

  return (
    <Box>
      <Typography className={classes.title} variant="subtitle1">
        {t('RESTORATION_STATUS')}
      </Typography>
      <Stepper
        className={classes.stepper}
        activeStep={activeStep}
        orientation="vertical"
        connector={<StatusConnector />}
      >
        {steps.map((currStep, index) => (
          <Step key={currStep}>
            <StepLabel
              StepIconComponent={stepIconProps => (
                <DetailStepIcon
                  {...stepIconProps}
                  isDashboard={props?.isDashboard}
                />
              )}
            >
              <Typography
                className={clsx(classes.stepsName, {
                  [props?.isDashboard
                    ? classes.activeStepDashboardAlt
                    : classes.activeStep]: activeStep === index,
                  [classes.completedStep]: activeStep > index,
                })}
                data-testid={`${
                  activeStep === index ? 'activeStep' : 'otherStep-' + index
                }`}
              >
                {currStep}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default RestorationStatus;
